import { hasAnyRole, isPurchaserOnly, isReadOnlyExecutive, OperationsData, OperationsPrincipals } from "../../auth/roles";
import type { GlobalSearchResponse } from "../../api/headerApi";
import { screensFor, type NavItem } from "../hub/hubs";

// Release 1.5.0 header search (O-R52): the pure model behind the Ctrl+K box — recent items first, then the server's
// groups (each authorised per person on the server), then the person's own pages, and "Ask VIPER" last. The record
// kinds a role may receive mirror GlobalSearchRules.AllowedCategories so recent items never show what the role cannot open.

export type RecordKind = "workOrders" | "parts" | "customers" | "workcenters" | "machines" | "purchaseOrders" | "knowledge" | "procedures" | "page";

/** The record kinds a person may receive (the server decides; this mirror only filters recent items). */
export function searchKindsFor(roles: readonly string[]): RecordKind[] {
  const principals = roles.filter((r) => (OperationsPrincipals as readonly string[]).includes(r));
  if (principals.length === 0) return [];
  const kinds: RecordKind[] = [];
  if (hasAnyRole(roles, OperationsData)) kinds.push("workOrders", "parts", "customers", "workcenters", "machines", "purchaseOrders");
  kinds.push("knowledge", "procedures");
  if (isReadOnlyExecutive(roles)) return kinds.filter((k) => k === "workOrders" || k === "knowledge" || k === "procedures");
  if (isPurchaserOnly(roles)) return kinds.filter((k) => k !== "customers" && k !== "workcenters" && k !== "machines");
  return kinds;
}

// ── Recent items: per person, per browser (shared shop-floor PCs never show one person's history to the next) ──────

export interface RecentItem {
  kind: RecordKind;
  to: string;
  label: string;
  detail?: string | null;
  at: number;
}

export const RECENT_PREFIX = "summitops.recent";
export const RECENT_MAX = 8;

export function recentKey(person: string | null | undefined): string {
  return `${RECENT_PREFIX}.${(person ?? "anonymous").toLowerCase()}`;
}

export function readRecent(person: string | null | undefined): RecentItem[] {
  try {
    const raw = window.localStorage.getItem(recentKey(person));
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter((r): r is RecentItem => !!r && typeof r === "object" && typeof (r as RecentItem).to === "string" && typeof (r as RecentItem).label === "string")
      : [];
  } catch {
    return [];
  }
}

/** Remember an opened item (newest first, no duplicates). `keepLabel`: a richer label already stored wins. */
export function recordRecent(person: string | null | undefined, item: Omit<RecentItem, "at">, keepLabel = false): void {
  try {
    const existing = readRecent(person);
    const before = existing.find((r) => r.to === item.to);
    const entry: RecentItem = keepLabel && before ? { ...before, at: Date.now() } : { ...item, at: Date.now() };
    const next = [entry, ...existing.filter((r) => r.to !== item.to)].slice(0, RECENT_MAX);
    window.localStorage.setItem(recentKey(person), JSON.stringify(next));
  } catch {
    // Storage can be unavailable (private window); recent items are a convenience only.
  }
}

/** A record page the person just opened, as a recent item; null for any other path. */
export function recentFromPath(pathname: string): Omit<RecentItem, "at"> | null {
  const decode = (s: string) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  };
  let m = /^\/work-orders\/([^/]+)$/.exec(pathname);
  if (m) {
    const key = decode(m[1]!);
    return { kind: "workOrders", to: pathname, label: `Work order ${key.replace("|", "-")}` };
  }
  m = /^\/customers\/([^/]+)$/.exec(pathname);
  if (m && m[1] !== "my-briefs") return { kind: "customers", to: pathname, label: `Customer ${decode(m[1]!)}` };
  m = /^\/knowledge\/base\/(\d+)$/.exec(pathname);
  if (m) return { kind: "knowledge", to: pathname, label: `Knowledge article ${m[1]}` };
  m = /^\/knowledge\/procedures\/(\d+)$/.exec(pathname);
  if (m) return { kind: "procedures", to: pathname, label: `Procedure ${m[1]}` };
  return null;
}

/** Recent items this person may still open (a role can change between visits). */
export function permittedRecent(items: readonly RecentItem[], roles: readonly string[]): RecentItem[] {
  const kinds = new Set(searchKindsFor(roles));
  const pages = new Set(screensFor(roles).map((s) => s.to));
  return items.filter((r) => (r.kind === "page" ? pages.has(r.to) : kinds.has(r.kind)));
}

// ── Options ──────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface SearchOption {
  key: string;
  group: string;
  groupLabel: string;
  label: string;
  detail?: string | null;
  badge?: string | null;
  to?: string;
  kind: RecordKind | "ask";
}

export interface SearchGroupView {
  id: string;
  label: string;
  truncated: boolean;
  options: SearchOption[];
}

/** Words all present (any order, any case). */
export function matchesWords(haystack: string, query: string): boolean {
  const hay = haystack.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}

/** The person's own pages that match, labels starting with the query first. */
export function matchPages(screens: readonly NavItem[], query: string, limit = 5): NavItem[] {
  const q = query.trim();
  if (!q) return [];
  const hits = screens.filter((s) => matchesWords(`${s.label} ${s.to.replaceAll("/", " ")}`, q));
  const lower = q.toLowerCase();
  return [...hits.filter((s) => s.label.toLowerCase().startsWith(lower)), ...hits.filter((s) => !s.label.toLowerCase().startsWith(lower))].slice(0, limit);
}

export interface BuildSearchInput {
  query: string;
  recent: readonly RecentItem[];
  server: GlobalSearchResponse | null | undefined;
  screens: readonly NavItem[];
  canAskViper: boolean;
}

/** The grouped options in display order and the flat list the arrow keys walk. */
export function buildSearchOptions({ query, recent, server, screens, canAskViper }: BuildSearchInput): { groups: SearchGroupView[]; flat: SearchOption[] } {
  const q = query.trim();
  const groups: SearchGroupView[] = [];
  const seen = new Set<string>();

  const recentHits = (q ? recent.filter((r) => matchesWords(`${r.label} ${r.detail ?? ""}`, q)) : recent).slice(0, 5);
  if (recentHits.length > 0) {
    groups.push({
      id: "recent",
      label: "Recent",
      truncated: false,
      options: recentHits.map((r) => {
        seen.add(`${r.kind}:${r.to}`);
        return { key: `recent:${r.to}`, group: "recent", groupLabel: "Recent", label: r.label, detail: r.detail ?? null, to: r.to, kind: r.kind };
      })
    });
  }

  if (q && server) {
    for (const g of server.groups) {
      const options = g.items
        .filter((i) => !seen.has(`${g.category}:${i.to}`))
        .map<SearchOption>((i) => ({
          key: i.id,
          group: g.category,
          groupLabel: g.label,
          label: i.title,
          detail: i.subtitle,
          badge: i.badge,
          to: i.to,
          kind: g.category as RecordKind
        }));
      if (options.length > 0) groups.push({ id: g.category, label: g.label, truncated: g.truncated, options });
    }
  }

  if (q) {
    const pages = matchPages(screens, q).filter((p) => !seen.has(`page:${p.to}`));
    if (pages.length > 0) {
      groups.push({
        id: "pages",
        label: "Pages",
        truncated: false,
        options: pages.map((p) => ({ key: `page:${p.to}`, group: "pages", groupLabel: "Pages", label: p.label, detail: p.to, to: p.to, kind: "page" as const }))
      });
    }
    if (canAskViper) {
      groups.push({
        id: "ask",
        label: "VIPER",
        truncated: false,
        options: [{ key: "ask", group: "ask", groupLabel: "VIPER", label: `Ask VIPER: “${q}”`, detail: "VIPER explains; it never changes anything", kind: "ask" }]
      });
    }
  }

  return { groups, flat: groups.flatMap((g) => g.options) };
}

/** May this person ask VIPER from the search box? (The Ask VIPER page's own rule.) */
export function canAskViper(roles: readonly string[]): boolean {
  return screensFor(roles).some((s) => s.to === "/viper");
}
